/**
 * How the Areas tab is read: presence, not a GPS heartbeat log.
 *
 * geofence_activity writes an enter on many ticks while a device stays
 * inside. Listing those is why one stay looks like 80 arrivals. A person
 * who has not left is one occupancy; an exit closes it. Heartbeats only
 * refresh last-seen.
 */
import { MissionGeofenceEvent, MissionRouteEvent } from '../../services/missionApi';

export interface AreaVisit {
  enteredAt: string;
  leftAt?: string;
  latitude: number;
  longitude: number;
}

export interface AreaPresence {
  key: string;
  geofenceId: number;
  geofenceName: string;
  entity: string;
  deviceId: string;
  inside: boolean;
  enteredAt: string;
  lastAt: string;
  leftAt?: string;
  visits: number;
  history: AreaVisit[];
  latitude: number;
  longitude: number;
  watchlisted: boolean;
}

export interface AreaPresenceFeed {
  presence: AreaPresence[];
  inside: number;
  visits: number;
  left: number;
  watchlisted: number;
}

type RosterAssignee = {
  device_id: string;
  entity_id?: string | null;
  label?: string | null;
};

type CatalogDevice = { device_id: string; label?: string | null };

/**
 * Beluga GPS publishes the radio serial. Subject-options only indexes the
 * phone, so Fences has to read the mission assignee row to get the operator.
 */
export function rosterDeviceLabel(
  deviceId: string,
  assignees: RosterAssignee[] = [],
  catalog: CatalogDevice[] = [],
): string {
  const assignee = assignees.find((row) => row.device_id === deviceId);
  const entity = (assignee?.entity_id || '').trim();
  if (entity) return entity;
  const named = (assignee?.label || '').split(' · ')[0].trim();
  if (named) return named;
  return (catalog.find((row) => row.device_id === deviceId)?.label || '').trim() || deviceId;
}

function occupant(
  event: MissionGeofenceEvent,
  deviceLabel: (deviceId: string) => string,
): string {
  const labeled = (deviceLabel(event.device_id) || '').trim();
  const recorded = (event.entity_id || '').trim();
  if (labeled && labeled !== event.device_id) return labeled;
  return recorded || labeled || event.device_id;
}

export function buildAreaPresence(
  events: MissionGeofenceEvent[],
  deviceLabel: (deviceId: string) => string,
  watchlist: string[] = [],
): AreaPresenceFeed {
  const watched = new Set(watchlist.map((name) => name.trim().toLowerCase()).filter(Boolean));
  const buckets = new Map<string, MissionGeofenceEvent[]>();

  for (const event of events) {
    const entity = occupant(event, deviceLabel);
    const key = `${event.geofence_id}:${entity.toLowerCase()}`;
    const bucket = buckets.get(key) || [];
    bucket.push(event);
    buckets.set(key, bucket);
  }

  const presence: AreaPresence[] = [];

  for (const bucket of buckets.values()) {
    const ordered = [...bucket].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
    const latest = ordered[ordered.length - 1];
    const entity = occupant(latest, deviceLabel);
    const visits: AreaVisit[] = [];

    for (const event of ordered) {
      const open = visits[visits.length - 1];
      if (event.event_type === 'enter') {
        if (open && !open.leftAt) continue;
        visits.push({
          enteredAt: event.timestamp,
          latitude: event.latitude,
          longitude: event.longitude,
        });
      } else if (open && !open.leftAt) {
        open.leftAt = event.timestamp;
      } else {
        visits.push({
          enteredAt: event.timestamp,
          leftAt: event.timestamp,
          latitude: event.latitude,
          longitude: event.longitude,
        });
      }
    }

    const current = visits[visits.length - 1];
    if (!current) continue;
    presence.push({
      key: `${latest.geofence_id}:${entity.toLowerCase()}`,
      geofenceId: latest.geofence_id,
      geofenceName: latest.geofence_name,
      entity,
      deviceId: latest.device_id,
      inside: !current.leftAt,
      enteredAt: current.enteredAt,
      lastAt: latest.timestamp,
      leftAt: current.leftAt,
      visits: visits.length,
      history: visits,
      latitude: current.latitude,
      longitude: current.longitude,
      watchlisted: watched.has(entity.toLowerCase()),
    });
  }

  presence.sort((a, b) => {
    if (a.watchlisted !== b.watchlisted) return a.watchlisted ? -1 : 1;
    if (a.inside !== b.inside) return a.inside ? -1 : 1;
    return new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime();
  });

  return {
    presence,
    inside: presence.filter((row) => row.inside).length,
    visits: presence.reduce((sum, row) => sum + row.visits, 0),
    left: presence.filter((row) => !row.inside).length,
    watchlisted: presence.filter((row) => row.watchlisted).length,
  };
}

export function areaBadgeCount(feed: AreaPresenceFeed): number {
  return feed.watchlisted || feed.inside;
}

export function watchlistLabels(events: MissionRouteEvent[] | undefined): string[] {
  const names = new Set<string>();
  for (const event of events || []) {
    if (event.event_type !== 'watchlist_on_route') continue;
    const name = (event.label || '').trim();
    if (name) names.add(name);
  }
  return [...names];
}
